// =====================================================================
// CompletionModal.jsx  -  Day 11 (28-05-2026)
// Modal "Terminer le trajet" — variant B (cards mobile-first).
//
// Replace window.prompt() chain dans MesCourses.handleTerminer.
// Inputs per line : km (chauffeur) + durée (chauffeur).
// Prix achat/vente NON éditables ici — restent du ressort de l'encodeur.
//
// Props:
//   - trip         : trip object (with .lines)
//   - onClose()    : fermer sans confirmer
//   - onSubmit(payload) : payload = { kmTotal, lines: [{id, durationMinutes, km}], driverComment }
//   - submitting   : bool — désactive Confirmer pendant async
// =====================================================================

import { useState, useMemo, useEffect } from 'react';

export default function CompletionModal({ trip, onClose, onSubmit, submitting }) {
  // line state map by id
  const lineDefs = useMemo(() => {
    return Array.isArray(trip.lines) ? trip.lines : [];
  }, [trip]);

  const [lineState, setLineState] = useState(() => {
    const m = {};
    for (const l of lineDefs) {
      m[l.id] = {
        km: l.km != null && l.km > 0 ? String(l.km) : '',
        duration: l.durationMinutes != null && l.durationMinutes > 0 ? String(l.durationMinutes) : '',
      };
    }
    return m;
  });
  const [comment, setComment] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Reset on trip change
  useEffect(() => {
    const m = {};
    for (const l of lineDefs) {
      m[l.id] = {
        km: l.km != null && l.km > 0 ? String(l.km) : '',
        duration: l.durationMinutes != null && l.durationMinutes > 0 ? String(l.durationMinutes) : '',
      };
    }
    setLineState(m);
    setComment('');
    setErrorMsg('');
  }, [trip?.id, lineDefs]);

  function update(id, field, value) {
    setLineState((s) => ({ ...s, [id]: { ...s[id], [field]: value } }));
  }

  // Sums for footer
  const totals = useMemo(() => {
    let km = 0, dur = 0, missing = 0;
    for (const l of lineDefs) {
      const ls = lineState[l.id] || {};
      const k = parseInt(ls.km, 10);
      const d = parseInt(ls.duration, 10);
      if (!isNaN(k)) km += k; else missing++;
      if (!isNaN(d) && d > 0) dur += d; else missing++;
    }
    return { km, dur, missing };
  }, [lineState, lineDefs]);

  function formatDuration(min) {
    if (!min) return '0h00';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  }

  function handleConfirm() {
    setErrorMsg('');
    const lines = [];
    for (const l of lineDefs) {
      const ls = lineState[l.id] || {};
      const km = parseInt(ls.km, 10);
      const duration = parseInt(ls.duration, 10);
      if (isNaN(duration) || duration < 1 || duration > 1440) {
        setErrorMsg(`Durée invalide pour ${l.project?.name || 'la ligne'}`);
        return;
      }
      if (isNaN(km) || km < 0 || km > 99999) {
        setErrorMsg(`Km invalides pour ${l.project?.name || 'la ligne'}`);
        return;
      }
      lines.push({ id: l.id, durationMinutes: duration, km });
    }
    const payload = {
      kmTotal: totals.km,  // denorm sum (backend recomputes too)
      lines,
      driverComment: comment.trim() || undefined,
    };
    onSubmit(payload);
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} className="modal-mobile-fullscreen" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <div style={refBadgeStyle}>{trip.ref}</div>
            <h2 style={titleStyle}>Terminer le trajet</h2>
            <div style={routeStyle}>
              <strong>{trip.origin}</strong>
              <span style={{ margin: '0 8px', color: '#9ca3af' }}>→</span>
              <strong>{trip.destination}</strong>
            </div>
          </div>
          <button style={closeBtnStyle} onClick={onClose} aria-label="Fermer">×</button>
        </div>

        {/* Body: cards per ligne */}
        <div style={bodyStyle}>
          <div style={instructionStyle}>
            Saisissez les <strong>km parcourus</strong> et la <strong>durée</strong> pour chaque projet.
          </div>

          {lineDefs.map((l, idx) => {
            const ls = lineState[l.id] || {};
            return (
              <div key={l.id} style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <span style={cardIdxStyle}>{idx + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={projectNameStyle}>{l.project?.name || 'Projet sans nom'}</div>
                    {l.taskTemplate?.label && (
                      <div style={taskLabelStyle}>{l.taskTemplate.label}</div>
                    )}
                    {l.info && (
                      <div style={infoLabelStyle}>{l.info}</div>
                    )}
                  </div>
                </div>

                <div style={inputsRowStyle}>
                  <div style={inputBlockStyle}>
                    <label style={labelStyle}>Km</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="99999"
                      placeholder="0"
                      value={ls.km || ''}
                      onChange={(e) => update(l.id, 'km', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={inputBlockStyle}>
                    <label style={labelStyle}>Durée (min)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="1440"
                      placeholder="60"
                      value={ls.duration || ''}
                      onChange={(e) => update(l.id, 'duration', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Commentaire optionnel */}
          <div style={commentBlockStyle}>
            <label style={commentLabelStyle}>Commentaire (optionnel)</label>
            <textarea
              maxLength={500}
              placeholder="Notes pour l'encodeur (incident, attente, etc.)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={textareaStyle}
              rows={2}
            />
          </div>
        </div>

        {/* Footer: totals + actions */}
        <div style={footerStyle}>
          <div style={totalsRowStyle}>
            <div style={totalBlockStyle}>
              <div style={totalLabelStyle}>Km total</div>
              <div style={totalValueStyle}>{totals.km}</div>
            </div>
            <div style={totalBlockStyle}>
              <div style={totalLabelStyle}>Durée totale</div>
              <div style={totalValueStyle}>{formatDuration(totals.dur)}</div>
            </div>
          </div>

          {errorMsg && (
            <div style={errorStyle}>{errorMsg}</div>
          )}

          <div style={actionsRowStyle}>
            <button style={cancelBtnStyle} onClick={onClose} disabled={submitting}>
              Annuler
            </button>
            <button
              style={submitting ? confirmBtnDisabledStyle : confirmBtnStyle}
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? 'Envoi…' : 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, padding: '8px',
};
const modalStyle = {
  background: '#fff', borderRadius: '12px',
  width: '100%', maxWidth: '480px',
  maxHeight: '95vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
};
const headerStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '12px',
  padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb',
};
const refBadgeStyle = {
  display: 'inline-block', fontSize: '11px', fontWeight: 700,
  padding: '2px 8px', borderRadius: '6px',
  background: '#eef2ff', color: '#4338ca',
  marginBottom: '4px',
};
const titleStyle = {
  margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827',
};
const routeStyle = {
  marginTop: '4px', fontSize: '13px', color: '#4b5563',
};
const closeBtnStyle = {
  background: 'transparent', border: 'none',
  fontSize: '28px', lineHeight: 1, cursor: 'pointer',
  color: '#6b7280', padding: '0 4px',
};
const bodyStyle = {
  flex: 1, overflowY: 'auto', padding: '14px 18px',
};
const instructionStyle = {
  fontSize: '13px', color: '#4b5563', marginBottom: '12px',
  background: '#f9fafb', padding: '10px 12px', borderRadius: '8px',
  borderLeft: '3px solid #3b82f6',
};
const cardStyle = {
  background: '#fff', border: '1px solid #e5e7eb',
  borderRadius: '10px', padding: '12px',
  marginBottom: '10px',
};
const cardHeaderStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '10px',
  marginBottom: '10px',
};
const cardIdxStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: '24px', height: '24px',
  background: '#3b82f6', color: '#fff',
  borderRadius: '50%', fontSize: '12px', fontWeight: 700,
  flexShrink: 0, marginTop: '1px',
};
const projectNameStyle = {
  fontSize: '15px', fontWeight: 600, color: '#111827',
};
const taskLabelStyle = {
  fontSize: '12px', color: '#6b7280', marginTop: '2px',
};
const infoLabelStyle = {
  fontSize: '12px', color: '#9ca3af', marginTop: '2px',
  fontStyle: 'italic',
};
const inputsRowStyle = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
};
const inputBlockStyle = {
  display: 'flex', flexDirection: 'column',
};
const labelStyle = {
  fontSize: '11px', fontWeight: 600, color: '#6b7280',
  marginBottom: '4px', textTransform: 'uppercase',
  letterSpacing: '0.4px',
};
const inputStyle = {
  border: '1px solid #d1d5db', borderRadius: '8px',
  padding: '10px 12px', fontSize: '16px', fontWeight: 600,
  width: '100%', boxSizing: 'border-box',
  textAlign: 'center', color: '#111827',
};
const commentBlockStyle = {
  marginTop: '6px',
};
const commentLabelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 600,
  color: '#6b7280', marginBottom: '4px',
  textTransform: 'uppercase', letterSpacing: '0.4px',
};
const textareaStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: '8px',
  padding: '8px 10px', fontSize: '14px',
  fontFamily: 'inherit', resize: 'vertical',
};
const footerStyle = {
  borderTop: '1px solid #e5e7eb', padding: '12px 18px 16px',
  background: '#f9fafb', borderRadius: '0 0 12px 12px',
};
const totalsRowStyle = {
  display: 'grid', gridTemplateColumns: '1fr 1fr',
  gap: '8px', marginBottom: '12px',
};
const totalBlockStyle = {
  background: '#fff', border: '1px solid #e5e7eb',
  borderRadius: '8px', padding: '8px 12px',
  textAlign: 'center',
};
const totalLabelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.4px',
};
const totalValueStyle = {
  fontSize: '20px', fontWeight: 700, color: '#111827',
  marginTop: '2px',
};
const errorStyle = {
  background: '#fef2f2', color: '#b91c1c',
  padding: '8px 12px', borderRadius: '6px',
  fontSize: '13px', marginBottom: '10px',
  border: '1px solid #fecaca',
};
const actionsRowStyle = {
  display: 'flex', gap: '10px',
};
const cancelBtnStyle = {
  flex: 1, padding: '12px 16px',
  background: '#fff', border: '1px solid #d1d5db',
  borderRadius: '8px', fontSize: '15px', fontWeight: 600,
  color: '#374151', cursor: 'pointer',
};
const confirmBtnStyle = {
  flex: 2, padding: '12px 16px',
  background: '#10b981', border: 'none',
  borderRadius: '8px', fontSize: '15px', fontWeight: 700,
  color: '#fff', cursor: 'pointer',
};
const confirmBtnDisabledStyle = {
  ...confirmBtnStyle,
  background: '#9ca3af', cursor: 'not-allowed',
};
